import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { PlainTooltip } from "@similarweb/ui-components";

import ChangeIndication from "app/components/ChangeIndication/ChangeIndication";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { percentageSignFilter } from "app/filters/ngFilters";

const ChangeIndicatorWrapper = styled.span`
    margin-bottom: 8px;
`;

// absolute value is used because we don't need to show the sign of the change (percentageSignFilter adds "-" sign for negative values)
const volumeChangeFormatter = (value: number | null): string =>
    value ? percentageSignFilter()(Math.abs(value), 2) : "N/A";

type SearchVolumeOverTimeChangeIndicatorProps = {
    isLoading: boolean;
    changeValue: number | null;
    volumeGrowth: string | null;
};

export const SearchVolumeOverTimeChangeIndicator = ({
    isLoading,
    changeValue,
    volumeGrowth,
}: SearchVolumeOverTimeChangeIndicatorProps) => {
    const t = useTranslation();
    const { params } = useCurrentStateAndParams();
    const { duration, comparedDuration } = params;
    const isPeriodOverPeriod = DurationService.isPeriodOverPeriod();
    const isYearOverYear = comparedDuration === BasicDurations.LAST_TWELVE_MONTHS;

    if (isLoading) {
        return null;
    }

    if (!changeValue) {
        return (
            <PlainTooltip
                placement="top"
                maxWidth={330}
                tooltipContent={t("demand.analysis.search.trends.trends.overview.change.na.tooltip")}
            >
                <ChangeIndicatorWrapper>
                    <ChangeIndication
                        isNotChanged
                        changeValue={t("apps.common.na")}
                        isIncreasing={false}
                    />
                </ChangeIndicatorWrapper>
            </PlainTooltip>
        );
    }

    const formattedChange = volumeChangeFormatter(changeValue);
    let change: string;
    if (!isPeriodOverPeriod || isYearOverYear) {
        change = t("demand.analysis.search.trends.trends.overview.change.yoy", { change: formattedChange });
    } else {
        change = t("demand.analysis.search.trends.trends.overview.change.pop", { change: formattedChange });
    }

    const tooltipContent = t("demand.analysis.search.trends.trends.overview.change.pop.tooltip");

    return (
        <PlainTooltip
            placement="top"
            maxWidth={325}
            tooltipContent={tooltipContent}
        >
            <ChangeIndicatorWrapper>
                <ChangeIndication
                    granularity={null}
                    changeValue={change}
                    isIncreasing={volumeGrowth == "Growing" || changeValue > 0}
                    isNotChanged={volumeGrowth === "Stagnating"}
                    forceShowArrows={volumeGrowth === "Stagnating"}
                />
            </ChangeIndicatorWrapper>
        </PlainTooltip>
    );
};
